import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, X, AlertTriangle, OctagonX } from 'lucide-react';
import { getActiveNotices, getDismissedIds, dismissNotice } from '../services/noticeService';
import { trackEvent } from '../lib/analytics';
import './NoticeBanner.css';
import { ICON_SIZE } from '../design-system';

/**
 * NoticeBanner — remote notice channel (ADR-007, P4-d).
 * Fetches once per boot; shows the newest non-dismissed notice.
 * severity=block is not dismissible. Copy comes from the DB (no i18n keys).
 */
const SEVERITY_ICON = {
  warn: AlertTriangle,
  block: OctagonX,
};

const NoticeBanner = () => {
  const { t } = useTranslation('common');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getActiveNotices().then((notices) => {
      if (cancelled || !notices.length) return;
      const dismissed = getDismissedIds();
      const visible = notices.find(
        (n) => n.severity === 'block' || !dismissed.includes(n.id),
      );
      if (visible) {
        setNotice(visible);
        trackEvent('notice_banner_shown', { noticeId: visible.id, severity: visible.severity });
      }
    });
    return () => { cancelled = true; };
  }, []);

  if (!notice) return null;

  const Icon = SEVERITY_ICON[notice.severity] || Megaphone;
  const dismissible = notice.severity !== 'block';

  return (
    <div className={`notice-banner notice-banner--${notice.severity}`}>
      <div className="notice-banner-content">
        <Icon size={ICON_SIZE.xs} className="notice-banner-icon" />
        <span className="notice-banner-text">
          <strong>{notice.title}</strong>
          {notice.body ? ` — ${notice.body}` : ''}
        </span>
      </div>
      {dismissible && (
        <button
          className="notice-banner-close"
          aria-label={t('close', { defaultValue: 'Close' })}
          onClick={() => { dismissNotice(notice.id); setNotice(null); }}
        >
          <X size={ICON_SIZE.xs} />
        </button>
      )}
    </div>
  );
};

export default NoticeBanner;
